/* ============================================================
   SPICY DADDA, Product Detail Page (PDP) interaction layer
   1. Shared shell: hamburger menu + footer newsletter
   2. Gallery: thumbnail switching, hover-magnify, mobile swipe
   3. Quantity stepper
   4. Add to Cart, the "Beam Aboard" tractor-beam sequence
   5. Sticky add-to-cart bar (appears on scroll past the buy box)
   ============================================================ */
(() => {
  'use strict';

  const prefersReducedMotion =
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;


  /* ------------------------------------------------------------
     1. SHARED SHELL, hamburger menu + footer newsletter
        (mirrors site.js so the PDP is self-contained)
     ------------------------------------------------------------ */
  const menuToggle = document.getElementById('menuToggle');
  const menuClose  = document.getElementById('menuClose');
  const appMenu    = document.getElementById('appMenu');

  const setMenu = (open) => {
    if (!appMenu) return;
    appMenu.classList.toggle('is-open', open);
    appMenu.setAttribute('aria-hidden', open ? 'false' : 'true');
    if (menuToggle) menuToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  };
  if (menuToggle) menuToggle.addEventListener('click', () => setMenu(true));
  if (menuClose)  menuClose.addEventListener('click', () => setMenu(false));
  if (appMenu) appMenu.addEventListener('click', (e) => {
    if (e.target.closest('a') || e.target === appMenu) setMenu(false);
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') setMenu(false); });

  const footerNews = document.getElementById('footerNews');
  if (footerNews) {
    footerNews.addEventListener('submit', (e) => {
      e.preventDefault();
      const btn = footerNews.querySelector('.footer-news__btn');
      const input = footerNews.querySelector('.footer-news__input');
      if (input) { input.value = ''; input.placeholder = 'Frequency locked ✓'; input.disabled = true; }
      if (btn) { btn.textContent = 'Joined'; btn.disabled = true; }
    });
  }


  /* ------------------------------------------------------------
     2. GALLERY, thumbnails, hover-magnify, mobile swipe
     ------------------------------------------------------------ */
  const stage   = document.getElementById('galleryStage');
  const mainImg = document.getElementById('galleryMain');
  const thumbs  = Array.from(document.querySelectorAll('.pdp-gallery__thumb'));

  const selectThumb = (idx) => {
    const thumb = thumbs[idx];
    if (!thumb || !mainImg) return;
    const full = thumb.dataset.full;
    if (full && mainImg.getAttribute('src') !== full) {
      // brief fade so the swap feels intentional, not a flicker
      mainImg.style.opacity = '0';
      const swap = () => {
        mainImg.src = full;
        requestAnimationFrame(() => { mainImg.style.opacity = '1'; });
        mainImg.removeEventListener('transitionend', swap);
      };
      if (prefersReducedMotion) { mainImg.src = full; mainImg.style.opacity = '1'; }
      else setTimeout(swap, 120);
    }
    thumbs.forEach((t, i) => {
      const on = i === idx;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
    });
  };

  thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => selectThumb(i)));

  // Hover-magnify (desktop pointers only)
  if (stage && mainImg && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    stage.addEventListener('mouseenter', () => {
      if (stage.classList.contains('is-beaming')) return;
      stage.classList.add('is-zooming');
      mainImg.style.transform = 'scale(1.6)';
    });
    stage.addEventListener('mousemove', (e) => {
      if (!stage.classList.contains('is-zooming')) return;
      const r = stage.getBoundingClientRect();
      const x = ((e.clientX - r.left) / r.width) * 100;
      const y = ((e.clientY - r.top) / r.height) * 100;
      mainImg.style.transformOrigin = `${x}% ${y}%`;
    });
    stage.addEventListener('mouseleave', () => {
      stage.classList.remove('is-zooming');
      mainImg.style.transform = '';
      mainImg.style.transformOrigin = 'center';
    });
  }

  // Mobile swipe between images
  if (stage) {
    let sx = 0, sy = 0, tracking = false;
    stage.addEventListener('touchstart', (e) => {
      tracking = true; sx = e.touches[0].clientX; sy = e.touches[0].clientY;
    }, { passive: true });
    stage.addEventListener('touchend', (e) => {
      if (!tracking) return;
      tracking = false;
      const dx = e.changedTouches[0].clientX - sx;
      const dy = e.changedTouches[0].clientY - sy;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;  // not a horizontal swipe
      const current = thumbs.findIndex((t) => t.classList.contains('is-active'));
      const next = dx < 0 ? current + 1 : current - 1;
      if (next >= 0 && next < thumbs.length) selectThumb(next);
    }, { passive: true });
  }


  /* ------------------------------------------------------------
     3. QUANTITY STEPPER
     ------------------------------------------------------------ */
  const qtyInput = document.getElementById('qtyInput');
  const clampQty = (n) => Math.max(1, Math.min(99, n || 1));

  document.querySelectorAll('.pdp-qty__btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const step = parseInt(btn.dataset.step, 10) || 0;
      qtyInput.value = clampQty(parseInt(qtyInput.value, 10) + step);
    });
  });
  if (qtyInput) {
    qtyInput.addEventListener('input', () => {
      qtyInput.value = qtyInput.value.replace(/[^0-9]/g, '');
    });
    qtyInput.addEventListener('blur', () => {
      qtyInput.value = clampQty(parseInt(qtyInput.value, 10));
    });
  }


  /* ------------------------------------------------------------
     4. ADD TO CART, the "Beam Aboard" sequence
        A green tractor beam descends over the bottle, a UFO drops
        in and lifts the specimen, the button sweeps lime + confirms,
        the cargo-bay count ticks up, and a toast acknowledges.
     ------------------------------------------------------------ */
  const form        = document.getElementById('purchaseForm');
  const atc         = document.getElementById('addToCart');
  const atcLabel    = atc ? atc.querySelector('.pdp-atc__label') : null;
  const stickyCount = document.getElementById('stickyCount');
  const toast       = document.getElementById('cartToast');

  let cart = 0;
  let beamTimer = null;
  let toastTimer = null;

  const showToast = (html) => {
    if (!toast) return;
    const msg = toast.querySelector('.pdp-toast__msg');
    if (msg) msg.innerHTML = html;
    toast.classList.add('is-visible');
    toast.setAttribute('aria-hidden', 'false');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      toast.classList.remove('is-visible');
      toast.setAttribute('aria-hidden', 'true');
    }, 2600);
  };

  const successMessage = (qty) => {
    const units = qty > 1 ? `${qty} bottles` : '1 bottle';
    return `<strong>Beamed up!</strong> ${units} added to your cart.`;
  };

  const updateCount = (n) => {
    if (!stickyCount) return;
    stickyCount.textContent = n;
    stickyCount.hidden = n <= 0;
  };

  // Plays the tractor-beam animation as the success state. Called only
  // after the real Shopify cart add (below) has already succeeded.
  const playBeamSequence = (qty) => {
    if (!atc) return;
    cart += qty;
    updateCount(cart);

    // Reduced motion: skip the spectacle, confirm instantly
    if (prefersReducedMotion) {
      if (atcLabel) atcLabel.textContent = '✓ Abducted!';
      atc.classList.add('is-secured');
      showToast(successMessage(qty));
      clearTimeout(beamTimer);
      beamTimer = setTimeout(() => {
        if (atcLabel) atcLabel.textContent = 'Add to Cart';
        atc.classList.remove('is-secured');
      }, 1800);
      return;
    }

    // Full sequence
    if (stage) stage.classList.add('is-beaming');
    atc.classList.add('is-beaming');
    if (atcLabel) atcLabel.textContent = 'Beaming up…';

    clearTimeout(beamTimer);
    beamTimer = setTimeout(() => {
      if (stage) stage.classList.remove('is-beaming');
      atc.classList.remove('is-beaming');
      atc.classList.add('is-secured');
      if (atcLabel) atcLabel.textContent = '✓ Abducted!';
      showToast(successMessage(qty));

      beamTimer = setTimeout(() => {
        atc.classList.remove('is-secured');
        if (atcLabel) atcLabel.textContent = 'Add to Cart';
      }, 1800);
    }, 1150);  // matches the CSS beam animation duration
  };

  // Real Shopify cart add via the Cart AJAX API, so the beam animation can
  // run as the success state instead of the browser doing a full reload.
  // openDrawer: when true, skips the beam toast and opens the cart drawer
  // once the item is in the cart (used by Express Checkout and More
  // payment options, so they land in the same styled drawer as everything
  // else, with its own large checkout button to proceed from there).
  const addToCart = (openDrawer = false) => {
    if (!form) return;
    const variantId = form.querySelector('[name="id"]')?.value;
    if (!variantId) return;
    const qty = qtyInput ? clampQty(parseInt(qtyInput.value, 10)) : 1;

    if (atc) atc.setAttribute('aria-disabled', 'true');

    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: qty }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          showToast(`<strong>Transmission failed.</strong> ${data.description || 'Could not add to cart.'}`);
          return;
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pdp', productVariantId: variantId, cartData: data });
        }
        if (openDrawer) {
          document.getElementById('cart-icon-bubble')?.click();
          return;
        }
        playBeamSequence(qty);
      })
      .catch(() => {
        showToast('<strong>Transmission failed.</strong> Check your connection and try again.');
      })
      .finally(() => {
        if (atc) atc.removeAttribute('aria-disabled');
      });
  };

  if (form) form.addEventListener('submit', (e) => { e.preventDefault(); addToCart(); });

  // Sticky-bar button shares the same real add-to-cart flow
  const stickyAdd = document.getElementById('stickyAdd');
  if (stickyAdd) stickyAdd.addEventListener('click', () => addToCart());

  // Express Checkout and "More payment options" add the item then open
  // the cart drawer, same as the header cart icon, instead of jumping
  // straight to checkout.
  document.querySelectorAll('.pdp-express, .pdp-more').forEach((b) => {
    b.addEventListener('click', () => addToCart(true));
  });


  /* ------------------------------------------------------------
     5. STICKY ADD-TO-CART BAR, reveal once the buy box CTA
        scrolls out of view (mobile conversion lever)
     ------------------------------------------------------------ */
  const stickyBar = document.getElementById('stickyBar');
  if (stickyBar && atc && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        // show the bar once the primary ATC has scrolled up out of the viewport
        const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        stickyBar.classList.toggle('is-visible', past);
        stickyBar.setAttribute('aria-hidden', past ? 'false' : 'true');
      });
    }, { threshold: 0 });
    io.observe(atc);
  }


  /* ------------------------------------------------------------
     6. REVIEWS, "Decrypt More Transmissions" (placeholder)
     ------------------------------------------------------------ */
  const moreReviews = document.querySelector('.pdp-reviews__more');
  if (moreReviews) {
    moreReviews.addEventListener('click', () => {
      moreReviews.textContent = 'All transmissions decrypted ✓';
      moreReviews.disabled = true;
      moreReviews.style.opacity = '0.7';
      moreReviews.style.cursor = 'default';
    });
  }


  /* ------------------------------------------------------------
     7. RELATED CAROUSEL, arrows + drag (same feel as the homepage)
     ------------------------------------------------------------ */
  const relGrid = document.getElementById('relatedGrid');
  if (relGrid) {
    const deck = relGrid.closest('.pdp-related__deck');
    const relPrev = deck && deck.querySelector('.deck-nav--prev');
    const relNext = deck && deck.querySelector('.deck-nav--next');
    const relPitch = () => relGrid.children.length > 1
      ? relGrid.children[1].offsetLeft - relGrid.children[0].offsetLeft
      : relGrid.clientWidth;
    // CSS scroll-behavior: smooth animates these assignments in real browsers
    const stepTo = (target) => {
      relGrid.scrollLeft = Math.max(0, Math.min(relGrid.scrollWidth - relGrid.clientWidth, target));
    };
    if (relPrev) relPrev.addEventListener('click', () => stepTo(relGrid.scrollLeft - relPitch()));
    if (relNext) relNext.addEventListener('click', () => stepTo(relGrid.scrollLeft + relPitch()));

    // centre the arrows on the card image (recompute as images/fonts settle)
    const placeArrows = () => {
      const frame = relGrid.querySelector('.rel-card__frame');
      if (!frame || !deck || !relPrev || !relNext) return;
      const top = frame.getBoundingClientRect().top - deck.getBoundingClientRect().top + frame.offsetHeight / 2;
      relPrev.style.top = relNext.style.top = top + 'px';
    };
    placeArrows();
    window.addEventListener('resize', placeArrows);
    relGrid.querySelectorAll('img').forEach((img) => { if (!img.complete) img.addEventListener('load', placeArrows, { once: true }); });

    // mouse drag-to-scroll (desktop); native touch handles mobile swipe
    let down = false, startX = 0, startScroll = 0, moved = false;
    relGrid.addEventListener('mousedown', (e) => { down = true; moved = false; startX = e.clientX; startScroll = relGrid.scrollLeft; });
    window.addEventListener('mousemove', (e) => { if (!down) return; const dx = e.clientX - startX; if (Math.abs(dx) > 4) moved = true; relGrid.scrollLeft = startScroll - dx; });
    window.addEventListener('mouseup', () => { down = false; });
    relGrid.addEventListener('click', (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); } }, true);
  }

})();


/* ============================================================
   PHASE 1 POLISH, global footer restructure
   (mirrors script.js / site.js so PDP pages get the same footer)
   ============================================================ */
(() => {
  const footer = document.querySelector('.site-footer');
  if (!footer) return;
  const cols = footer.querySelectorAll('.footer-col');
  const social = footer.querySelector('.footer-social');

  const nlCol = cols[0];
  if (nlCol) {
    const head = nlCol.querySelector('.footer-col__eyebrow');
    if (head) {
      head.textContent = window.matchMedia('(min-width: 1024px)').matches
        ? 'Join our mailing list for 15% off your next order.'
        : 'Join our mailing list for 15% off your next order';
      head.classList.add('footer-news__headline');
    }
    const body = nlCol.querySelector('.footer-col__body');
    if (body) body.remove();
    const form = nlCol.querySelector('.footer-news');
    if (form && social) form.insertAdjacentElement('afterend', social);
  }

  // Shipping & Operations column removed entirely (per punch-list).
  if (cols[1]) cols[1].remove();

  if (cols[2]) cols[2].remove();
})();


/* ============================================================
   CUSTOMER REVIEWS, rendered with our own card markup
   Judge.me's own widget supplies the data (window.jdgm.data) but
   stays fully hidden; we never render its visual markup, so it
   can never duplicate our heading or fight our layout. The
   "Write a Review" button proxies a click to Judge.me's hidden
   write-review link so the real submission flow still works.
   ============================================================ */
(() => {
  const grid = document.getElementById('customerReviewsGrid');
  if (!grid) return;
  const emptyMsg = document.getElementById('customerReviewsEmpty');
  const seeAllBtn = document.getElementById('customerReviewsSeeAll');
  const productId = grid.dataset.productId;

  const starSvg = (filled) => {
    const fillStyle = filled ? '' : ' style="fill: rgba(166, 206, 57, 0.25)"';
    return `<svg viewBox="0 0 24 24"${fillStyle}><polygon points="12 2 15 9 22 9.3 16.5 14 18.5 21 12 17 5.5 21 7.5 14 2 9.3 9 9"/></svg>`;
  };

  const escapeHtml = (str) => String(str || '').replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));

  const renderReview = (review) => {
    const article = document.createElement('article');
    article.className = 'pdp-review';

    const date = review.created_at
      ? new Date(review.created_at).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
      : '';
    const rating = Math.round(review.rating || 0);
    const stars = Array.from({ length: 5 }, (_, i) => starSvg(i < rating)).join('');
    const photoUrl = review.pictures_urls && review.pictures_urls[0];
    const photo = photoUrl
      ? `<img class="pdp-review__photo" src="${escapeHtml(photoUrl)}" alt="" loading="lazy" width="44" height="44" />`
      : '';

    article.innerHTML = `
      <div class="pdp-review__meta">
        <span>${escapeHtml(date)}</span>
        ${review.verified_buyer ? '<span class="pdp-review__verified">VERIFIED</span>' : ''}
      </div>
      <div class="pdp-review__stars">${stars}</div>
      <p class="pdp-review__text">${escapeHtml(review.body)}</p>
      <div class="pdp-review__foot">
        ${photo}
        <span class="pdp-review__author">${escapeHtml(review.reviewer_name) || 'Anonymous'}</span>
      </div>
    `;
    return article;
  };

  const render = (reviews) => {
    grid.querySelectorAll('.pdp-review').forEach((el) => el.remove());
    if (!reviews || reviews.length === 0) {
      if (emptyMsg) emptyMsg.hidden = false;
      return;
    }
    if (emptyMsg) emptyMsg.hidden = true;
    reviews.forEach((review) => grid.appendChild(renderReview(review)));
  };

  // jdgm.data is populated by an inline script Judge.me's own snippet
  // outputs into the page, but their core script (which defines window.jdgm)
  // may not have run yet at this exact point, so poll briefly before
  // falling back to the empty state.
  let attempts = 0;
  const tryRender = () => {
    const widgetData = window.jdgm && window.jdgm.data && window.jdgm.data.reviewWidget
      && window.jdgm.data.reviewWidget[productId];
    if (widgetData && widgetData.reviews) {
      render(widgetData.reviews);
      return;
    }
    attempts += 1;
    if (attempts < 15) {
      setTimeout(tryRender, 200);
    } else {
      render([]);
    }
  };
  tryRender();

  // render() above already shows every review Judge.me has; there's no
  // pagination, so "See All" just confirms that and disables itself.
  if (seeAllBtn) {
    seeAllBtn.addEventListener('click', () => {
      seeAllBtn.textContent = 'All reviews shown';
      seeAllBtn.disabled = true;
    });
  }
})();
